import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const GREEN = '#0B6B55';
const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function StickerPassportPage() {
  const { passportUuid } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/public/passports/${passportUuid}`)
      .then(async r => {
        if (r.status === 401) throw new Error('LOGIN_ONLY');
        if (!r.ok) throw new Error('NOT_FOUND');
        return r.json();
      })
      .then(setData)
      .catch(e => setError(e.message));
  }, [passportUuid]);

  const Card = ({ children }) => (
    <div className="rounded-xl bg-white p-5 mb-4 shadow-sm border" style={{ borderColor: '#E5E3DC' }}>{children}</div>
  );

  const shell = (inner) => (
    <div className="min-h-screen px-4 py-5" style={{ background: '#F4F3EF', fontFamily: "'IBM Plex Sans', sans-serif" }}
      data-testid="public-passport-page">
      <div className="max-w-md mx-auto">
        <div className="flex items-center justify-between mb-5">
          <span className="text-2xl font-bold" style={{ color: '#1B1D1C' }}>Track0tic</span>
          <span className="text-sm" style={{ color: '#6b7069' }}>Verified product</span>
        </div>
        {inner}
      </div>
    </div>
  );

  if (error === 'LOGIN_ONLY') {
    return shell(<Card><p className="font-bold text-lg" data-testid="login-only-msg">Authorised staff: log in to view</p>
      <p className="text-sm mt-2" style={{ color: '#6b7069' }}>This passport is restricted. No location is shown on this page.</p></Card>);
  }
  if (error) {
    return shell(<Card><p className="font-bold text-lg">Unknown passport</p>
      <p className="text-sm mt-2" style={{ color: '#6b7069' }}>This code does not match any Track0tic passport.</p></Card>);
  }
  if (!data) return shell(<p className="text-sm text-center" style={{ color: '#9a9e96' }}>Loading…</p>);

  const bad = data.state === 'EXPIRED' || data.state === 'VOIDED';
  const excursion = data.chain_excursion_noted || (data.last_leg && data.last_leg.result === 'EXCURSION');
  const statusStyle = bad ? { background: '#FBEBEB', color: '#8C2B2B' }
    : excursion ? { background: '#FBF3E6', color: '#8C6A2B' }
      : { background: '#E7F2EE', color: GREEN };

  return shell(
    <>
      <Card>
        {data.product ? (
          <>
            <h1 className="text-2xl font-bold" style={{ color: '#1B1D1C' }} data-testid="product-name">{data.product.product_name || 'Product'}</h1>
            <p className="text-sm" style={{ color: '#6b7069' }}>
              {data.product.batch_no ? `Batch ${data.product.batch_no}` : ''}{data.product.expiry_date ? ` · Exp ${data.product.expiry_date}` : ''} · Store {data.storage_range}</p>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold" style={{ color: '#1B1D1C' }} data-testid="shipment-code">Shipment {data.short_code}</h1>
            <p className="text-sm" style={{ color: '#6b7069' }}>Shipped by {data.shipped_by} · required {data.storage_range}</p>
          </>
        )}
        <div className="rounded-lg p-3 mt-3" style={statusStyle} data-testid="status-banner">
          <p className="font-bold">{data.state_label}</p>
          {data.last_leg?.delivered_at && <p className="text-sm">Delivered to you on {new Date(data.last_leg.delivered_at).toLocaleString()}</p>}
        </div>
      </Card>

      {data.last_leg && (
        <Card>
          <h2 className="font-bold text-lg mb-3" style={{ color: '#1B1D1C' }}>Last delivery leg</h2>
          <div className="text-sm space-y-2" data-testid="last-leg-card">
            {[['From', data.last_leg.from],
              ['Picked up', data.last_leg.picked_up_at ? new Date(data.last_leg.picked_up_at).toLocaleString() : '—'],
              ['Delivered', data.last_leg.delivered_at ? new Date(data.last_leg.delivered_at).toLocaleString() : 'In transit'],
              ['Temperature', data.last_leg.temp_min_seen != null ? `${data.last_leg.temp_min_seen}–${data.last_leg.temp_max_seen} °C` : '—']].map(([k, v]) => (
              <div key={k} className="flex justify-between"><span style={{ color: '#6b7069' }}>{k}</span><b style={{ color: '#1B1D1C' }}>{v}</b></div>
            ))}
          </div>
          <div className="rounded-lg border border-dashed mt-4 p-3" style={{ borderColor: '#D9D6CD' }}>
            {data.last_leg.chart?.length > 1 ? (
              <svg viewBox="0 0 300 80" className="w-full h-20">
                {(() => {
                  const pts = data.last_leg.chart;
                  const temps = pts.map(p => p.c);
                  const lo = Math.min(...temps) - 1, hi = Math.max(...temps) + 1;
                  const path = pts.map((p, i) =>
                    `${i === 0 ? 'M' : 'L'}${(i / (pts.length - 1)) * 300},${80 - ((p.c - lo) / (hi - lo)) * 70 - 5}`).join(' ');
                  return <path d={path} fill="none" stroke={GREEN} strokeWidth="2" />;
                })()}
              </svg>
            ) : (
              <p className="text-sm text-center py-6" style={{ color: '#9a9e96' }}>Temperature chart · this leg only</p>
            )}
          </div>
        </Card>
      )}

      {data.certificate_url && (
        <a href={`${API_BASE}${data.certificate_url}`} data-testid="download-certificate-btn"
          className="block w-full rounded-xl py-4 text-center text-white font-bold text-lg" style={{ background: GREEN }}>
          Download certificate</a>
      )}
      {data.disclaimer && <p className="text-xs mt-4 text-center" style={{ color: '#9a9e96' }}>{data.disclaimer}</p>}
      <p className="text-xs mt-6 text-center" style={{ color: '#9a9e96' }}>
        Earlier supply-chain stages are not shown here. No map, route or location is displayed.</p>
    </>
  );
}
